import { useNavigate } from "react-router-dom";
import { Boxes, Zap, Sparkles, FlaskConical, ArrowRight, Wand2, Swords, Camera } from "lucide-react";
import { AIResourcesShell } from "../../components/ai/AIResourcesShell";

const TOOLS = [
  {
    key: "opportunity-scanner", to: "/ai-resources/opportunity-scanner", icon: Camera, badge: "NEW",
    title: "AI Opportunity Scanner", line: "Turn anything you see into your next startup idea with AI.",
    desc: "Snap a photo of anything around you — AI finds the problems and generates buildable ideas.", cta: "Scan an Opportunity",
  },
  {
    key: "tool-stack", to: "/ai-resources/tool-stack", icon: Boxes,
    title: "AI Tool Stack Audit", line: "Build your perfect AI toolkit.",
    desc: "Stop paying for AI tools you don't need.", cta: "Start Audit",
  },
  {
    key: "workflow", to: "/ai-resources/workflow", icon: Zap,
    title: "AI Workflow", line: "Save hours every week with AI.",
    desc: "Discover AI workflows tailored to your work.", cta: "Start Workflow Audit",
  },
  {
    key: "tool-recommendations", to: "/ai-resources/tool-recommendations", icon: Sparkles,
    title: "AI Tool Recommendations", line: "Find the perfect AI tool.",
    desc: "Tell us the job — get the best tool for it.", cta: "Find a Tool",
  },
  {
    key: "validation-lab", to: "/ai-resources/validation-lab", icon: FlaskConical,
    title: "Validation Lab", line: "Validate your idea before you build.",
    desc: "Pressure-test demand, market and risks — get a full report.", cta: "Start Validation",
  },
  {
    key: "competitor-search", to: "/ai-resources/competitor-search", icon: Swords,
    title: "Competitor Search", line: "Know your competition before you build.",
    desc: "Find competitors, spot market gaps and how to stand out.", cta: "Search Competitors",
  },
];

export default function AIResourcesHome() {
  const navigate = useNavigate();
  return (
    <AIResourcesShell active="home" title="AI Resource Center" subtitle="Everything you need to build smarter with AI." icon={Wand2}>
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-8 md:p-10 relative overflow-hidden" data-testid="airesources-hero">
        <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-widest">AI Resource Center</p>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-2 max-w-xl">Everything you need to build smarter with AI.</h2>
        <p className="text-[#c9c39f] mt-3 max-w-lg">Discover the right AI tools, workflows, and resources to build faster.</p>
        <div className="flex flex-wrap gap-3 mt-6">
          <button onClick={() => navigate("/ai-resources/tool-stack")} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="hero-start-audit">
            Start AI Audit <ArrowRight size={15} />
          </button>
          <a href="#explore" className="inline-flex items-center gap-2 border border-white/25 rounded-full px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors" data-testid="hero-explore">
            Explore Resources
          </a>
        </div>
      </div>

      {/* Tool cards */}
      <div id="explore" className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-8">
        {TOOLS.map((t) => (
          <div key={t.key} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_16px_44px_rgb(0,0,0,0.08)] transition-all flex flex-col" data-testid={`airesources-card-${t.key}`}>
            <div className="w-11 h-11 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center"><t.icon size={20} /></div>
            <h3 className="font-display text-lg font-bold text-[#0F0F0F] mt-4 flex items-center gap-2">{t.title}{t.badge && <span className="text-[9px] font-extrabold bg-[#D4AF37] text-[#0F0F0F] px-1.5 py-0.5 rounded-full">{t.badge}</span>}</h3>
            <p className="text-sm font-semibold text-[#374151] mt-1">{t.line}</p>
            <p className="text-sm text-[#687280] mt-1 leading-snug flex-1">{t.desc}</p>
            <button
              onClick={() => navigate(t.to)}
              className={`mt-5 inline-flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-bold transition-colors ${
                t.soon ? "bg-gray-100 text-[#9aa1ab]" : "bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030]"
              }`}
              data-testid={`airesources-card-cta-${t.key}`}
            >
              {t.cta} {!t.soon && <ArrowRight size={15} />}
            </button>
          </div>
        ))}
      </div>
    </AIResourcesShell>
  );
}
